import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { ChevronRight, Download, Users, AlertTriangle, Loader2 } from 'lucide-react';
import { Button, Banner, Empty, ErrorBlock, Money, Pill, Skeleton, downloadFile, errMsg, fmtDate, useLoad } from '../kit';
import { API, FigureLine, LinkBtn, PeriodPicker, calendarPresets, fileLink, hoursText, plural, rangeLabel, viewLink } from './shared';

// Team: how each support coordinator is going in a period. One row per coordinator: hours logged
// (time entries by the date worked, in rounded minutes — the same minutes pay and claims use),
// billable share and value, active participants against their caseload target, participants without
// a note for 30 days, and where this pay period's hours are. A row opens to its participants.

export default function TeamTab({ period, onPeriod }) {
  const params = period.from && period.to ? { from: period.from, to: period.to } : null;
  const { data, loading, error, reload } = useLoad(`${API}/team`, params);
  const [busy, setBusy] = useState(false);

  const presets = useMemo(() => {
    const pays = ((data && data.pay_periods) || []).map((p, i) => ({
      key: `pay:${p.start}`, from: p.start, to: p.end,
      label: i === 0 ? 'This pay period' : i === 1 ? 'Last pay period' : `Pay period ${rangeLabel(p.start, p.end)}`,
    }));
    return [...pays, ...calendarPresets(data ? data.today : undefined)];
  }, [data]);
  const value = period.key === 'pay-current' && data ? { key: `pay:${data.pay_period.start}`, from: data.from, to: data.to } : period;

  const exportXlsx = async () => {
    setBusy(true);
    try {
      const name = await downloadFile(`${API}/team/export`, { params: { from: data.from, to: data.to } });
      toast.success(`Downloaded ${name}`);
    } catch (e) { toast.error(errMsg(e, 'Couldn’t make the Excel file. Please try again.')); }
    finally { setBusy(false); }
  };

  if (error && !data) return <ErrorBlock error={error} onRetry={reload} />;
  if (!data) return <TeamSkeleton />;
  const rows = data.rows || [];
  const t = data.totals || {};
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PeriodPicker value={value} onChange={onPeriod} presets={presets} />
        <div className="flex items-center gap-3">
          {loading && <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-500"><Loader2 size={13} className="animate-spin" aria-hidden /> Updating…</span>}
          <Button variant="secondary" icon={Download} onClick={exportXlsx} loading={busy} disabled={!rows.length}>Export to Excel</Button>
        </div>
      </div>

      {error && <Banner tone="red" title="Couldn’t update" action={<Button size="sm" variant="secondary" onClick={reload}>Try again</Button>}>{error}</Banner>}

      <section aria-label="Team totals" className="bg-white rounded-2xl border border-slate-200/80 px-4 sm:px-5 py-3.5">
        <p className="text-[13px] font-semibold text-slate-900 mb-1.5">{rangeLabel(data.from, data.to)}</p>
        <FigureLine items={[
          { label: 'Hours logged', value: hoursText(t.minutes) },
          { label: 'Billable', value: t.billable_pct == null ? '—' : `${t.billable_pct}%` },
          { label: 'Billable value', value: <Money value={t.billable_value} /> },
          { label: 'Active participants', value: t.active_participants || 0 },
          { label: 'No note in 30 days', value: t.notes_overdue || 0, tone: t.notes_overdue ? 'amber' : undefined },
        ]} />
      </section>

      {data.unassigned && data.unassigned.length > 0 && (
        <Banner tone="amber" title={`${plural(data.unassigned.length, 'active participant has', 'active participants have')} no coordinator`}
          action={<LinkBtn to={viewLink('view=engagements')} variant="secondary">Open Caseload</LinkBtn>}>
          {data.unassigned.slice(0, 5).map((u) => u.name).join(', ')}{data.unassigned.length > 5 ? ` and ${data.unassigned.length - 5} more` : ''}. Assign them so their notes and hours have an owner.
        </Banner>
      )}

      {!rows.length ? (
        <div className="bg-white rounded-2xl border border-slate-200/80">
          <Empty icon={Users} title="No support coordinators yet" action={<LinkBtn to={viewLink('view=engagements')} variant="secondary">Open Caseload</LinkBtn>}>
            Assign participants to a coordinator in Caseload, or add a coordinator profile in SC Hours. They show here with their hours.
          </Empty>
        </div>
      ) : (
        <>
          <TeamTable rows={rows} data={data} />
          <div className="lg:hidden space-y-3">{rows.map((r) => <TeamCard key={r.user_id} r={r} data={data} />)}</div>
          <p className="text-[12px] text-slate-500 px-1">
            Hours are time entries by the date worked, rounded the same way as pay and claims. Billable value is billable hours at each participant’s service agreement rate.
            “This pay period” is {rangeLabel(data.pay_period.start, data.pay_period.end)}.
          </p>
        </>
      )}
    </div>
  );
}

const TH = 'px-3 py-2 first:pl-4 last:pr-4 text-[12px] font-semibold text-slate-600 bg-slate-50/95 border-y border-slate-200/80 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap';
const TD = 'px-3 first:pl-4 last:pr-4 py-3 border-b border-slate-100 align-top text-[13px] text-slate-700';

function TeamTable({ rows, data }) {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (id) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const t = data.totals || {};
  return (
    <div className="hidden lg:block bg-white rounded-2xl border border-slate-200/80 p-2 overflow-x-auto">
      <table className="w-full border-separate border-spacing-0">
        <caption className="sr-only">Support coordinators, {rangeLabel(data.from, data.to)}</caption>
        <thead>
          <tr>
            <th scope="col" className={clsx(TH, 'text-left')}>Support coordinator</th>
            <th scope="col" className={clsx(TH, 'text-right')}>Hours</th>
            <th scope="col" className={clsx(TH, 'text-right')}>Billable</th>
            <th scope="col" className={clsx(TH, 'text-right')}>Billable value</th>
            <th scope="col" className={clsx(TH, 'text-right')}>Active participants</th>
            <th scope="col" className={clsx(TH, 'text-right')}>No note in 30 days</th>
            <th scope="col" className={clsx(TH, 'text-left')}>Hours this pay period</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isOpen = open.has(r.user_id);
            const panelId = `team-p-${r.user_id}`;
            return (
              <React.Fragment key={r.user_id}>
                <tr className={clsx(isOpen && 'bg-violet-50/40')}>
                  <td className={TD}>
                    <button type="button" onClick={() => toggle(r.user_id)} aria-expanded={isOpen} aria-controls={panelId}
                      className="inline-flex items-center gap-1.5 text-left font-semibold text-slate-900 rounded-lg hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                      <ChevronRight size={15} className={clsx('text-slate-500 transition-transform flex-shrink-0', isOpen && 'rotate-90')} aria-hidden />
                      {r.name}
                    </button>
                    <p className="pl-[21px] text-[12px] text-slate-500">{plural(r.participants.length, 'participant', 'participants')}{r.active_user ? '' : ' · inactive login'}</p>
                  </td>
                  <td className={clsx(TD, 'text-right tabular-nums')}>
                    <span className="font-semibold text-slate-900">{hoursText(r.minutes)}</span>
                    {r.hours_target != null && (
                      <div className="mt-1 ml-auto w-28">
                        <TargetBar value={r.minutes / 60} max={r.hours_target} label={`${r.name}: hours against target`} />
                        <p className="mt-0.5 text-[12px] text-slate-500">of {r.hours_target.toFixed(2)} h target</p>
                      </div>
                    )}
                  </td>
                  <td className={clsx(TD, 'text-right tabular-nums')}>
                    <span className="font-semibold text-slate-900">{r.billable_pct == null ? '—' : `${r.billable_pct}%`}</span>
                    <p className="text-[12px] text-slate-500">{hoursText(r.billable_minutes)}</p>
                  </td>
                  <td className={clsx(TD, 'text-right tabular-nums font-semibold text-slate-900')}><Money value={r.billable_value} /></td>
                  <td className={clsx(TD, 'text-right tabular-nums')}>
                    <span className="font-semibold text-slate-900">{r.active_participants}</span>
                    {r.caseload_target != null && (
                      <div className="mt-1 ml-auto w-28">
                        <TargetBar value={r.active_participants} max={r.caseload_target} label={`${r.name}: caseload against target`} />
                        <p className="mt-0.5 text-[12px] text-slate-500">of {r.caseload_target} target</p>
                      </div>
                    )}
                  </td>
                  <td className={clsx(TD, 'text-right tabular-nums')}>
                    {r.notes_overdue ? <span className="inline-flex items-center gap-1 font-semibold text-amber-800"><AlertTriangle size={13} aria-hidden />{r.notes_overdue}</span> : <span className="text-slate-500">0</span>}
                  </td>
                  <td className={TD}><PayCell r={r} /></td>
                </tr>
                {isOpen && (
                  <tr id={panelId}>
                    <td colSpan={7} className="px-4 pb-4 pt-1 border-b border-slate-100 bg-violet-50/40">
                      <ParticipantTable r={r} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="font-semibold text-slate-900">
            <td className="px-4 py-3 text-[13px]">Team total</td>
            <td className="px-3 py-3 text-[13px] text-right tabular-nums">{hoursText(t.minutes)}</td>
            <td className="px-3 py-3 text-[13px] text-right tabular-nums">{t.billable_pct == null ? '—' : `${t.billable_pct}%`}</td>
            <td className="px-3 py-3 text-[13px] text-right tabular-nums"><Money value={t.billable_value} /></td>
            <td className="px-3 py-3 text-[13px] text-right tabular-nums">{t.active_participants || 0}</td>
            <td className="px-3 py-3 text-[13px] text-right tabular-nums">{t.notes_overdue || 0}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Progress against a target. Not a warning: over or under target is for the manager to read, so the
// bar stays violet (the kit Meter turns amber and red near 100%, which suits budgets, not targets).
function TargetBar({ value, max, label }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={max || 0} aria-valuenow={Math.round((value || 0) * 100) / 100}
      className="h-1.5 w-full rounded-full bg-violet-100 overflow-hidden">
      <div className="h-full rounded-full bg-violet-500" style={{ width: `${pct}%` }} />
    </div>
  );
}

// This pay period's status, and a nudge when the last (ended) period still has hours in draft.
function PayCell({ r }) {
  const cur = r.pay.current; const prev = r.pay.previous;
  const late = prev.ended && prev.draft_minutes > 0;
  return (
    <div className="space-y-1">
      {cur.status === 'none' ? <span className="text-[12.5px] text-slate-500">No hours yet</span>
        : <Pill status={cur.status} label={cur.status === 'draft' ? `Draft · ${hoursText(cur.minutes)}` : `${cur.status_label} · ${hoursText(cur.minutes)}`} />}
      {late && <p className="text-[12px] font-semibold text-amber-800">Last period: {hoursText(prev.draft_minutes)} not submitted</p>}
      {!late && prev.status === 'submitted' && <p className="text-[12px] text-slate-600">Last period waiting for approval</p>}
    </div>
  );
}

function ParticipantTable({ r }) {
  if (!r.participants.length) return <p className="text-[13px] text-slate-600 py-2">No participants assigned and no time logged in this period.</p>;
  return (
    <div className="bg-white rounded-xl ring-1 ring-slate-200/80 overflow-x-auto">
      <table className="w-full text-[13px]">
        <caption className="sr-only">{r.name}’s participants</caption>
        <thead>
          <tr className="text-[12px] text-slate-600 border-b border-slate-200">
            <th scope="col" className="text-left font-semibold px-4 py-2">Participant</th>
            <th scope="col" className="text-right font-semibold px-3 py-2">Hours</th>
            <th scope="col" className="text-right font-semibold px-3 py-2">Billable hours</th>
            <th scope="col" className="text-right font-semibold px-3 py-2">Billable value</th>
            <th scope="col" className="text-left font-semibold px-3 py-2">Last note</th>
            <th scope="col" className="text-left font-semibold px-4 py-2">Plan end</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {r.participants.map((p) => (
            <tr key={p.coordination_id || p.client_id}>
              <td className="px-4 py-2">
                {p.coordination_id
                  ? <Link to={fileLink(p.coordination_id)} className="font-semibold text-slate-900 rounded hover:text-indigo-700 hover:underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">{p.name}</Link>
                  : <span className="font-semibold text-slate-900">{p.name}</span>}
                {!p.assigned && <span className="ml-1.5 text-[12px] text-slate-500">(not assigned to them)</span>}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{hoursText(p.minutes)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{hoursText(p.billable_minutes)}</td>
              <td className="px-3 py-2 text-right tabular-nums"><Money value={p.billable_value} /></td>
              <td className="px-3 py-2"><LastNote p={p} /></td>
              <td className="px-4 py-2 whitespace-nowrap">{p.plan_end ? fmtDate(p.plan_end) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LastNote({ p }) {
  if (!p.assigned) return <span className="text-slate-500">—</span>;
  if (p.note_overdue) {
    return (
      <span className="inline-flex items-center gap-1 font-semibold text-amber-800 whitespace-nowrap">
        <AlertTriangle size={13} aria-hidden />{p.last_note ? `${fmtDate(p.last_note)} (${p.days_since_note} days)` : 'No notes yet'}
      </span>
    );
  }
  return <span className="whitespace-nowrap">{p.last_note ? fmtDate(p.last_note) : 'No notes yet'}</span>;
}

// Phones: one card per coordinator, figures as a short list, participants behind a toggle.
function TeamCard({ r }) {
  const [open, setOpen] = useState(false);
  const panelId = `team-c-${r.user_id}`;
  return (
    <section aria-label={r.name} className="bg-white rounded-2xl border border-slate-200/80 px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-[15px] font-semibold text-slate-900 leading-6 min-w-0 break-words">{r.name}</h3>
        <PayCell r={r} />
      </div>
      <dl className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
        <Fact k="Hours" v={`${hoursText(r.minutes)}${r.hours_target != null ? ` of ${r.hours_target.toFixed(2)}` : ''}`} />
        <Fact k="Billable" v={r.billable_pct == null ? '—' : `${r.billable_pct}% · ${hoursText(r.billable_minutes)}`} />
        <Fact k="Billable value" v={<Money value={r.billable_value} />} />
        <Fact k="Active participants" v={`${r.active_participants}${r.caseload_target != null ? ` of ${r.caseload_target}` : ''}`} />
        <Fact k="No note in 30 days" v={r.notes_overdue} warn={r.notes_overdue > 0} />
      </dl>
      {r.participants.length > 0 && (
        <>
          <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls={panelId}
            className="mt-2 min-h-[44px] inline-flex items-center gap-1.5 text-[13px] font-semibold text-indigo-700 hover:text-indigo-900 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
            <ChevronRight size={15} className={clsx('transition-transform', open && 'rotate-90')} aria-hidden />
            {open ? 'Hide participants' : `Show participants (${r.participants.length})`}
          </button>
          {open && (
            <ul id={panelId} className="divide-y divide-slate-100 border-t border-slate-100">
              {r.participants.map((p) => (
                <li key={p.coordination_id || p.client_id} className="py-2.5">
                  {p.coordination_id
                    ? <Link to={fileLink(p.coordination_id)} className="font-semibold text-[13.5px] text-slate-900 hover:text-indigo-700">{p.name}</Link>
                    : <span className="font-semibold text-[13.5px] text-slate-900">{p.name}</span>}
                  <p className="text-[12.5px] text-slate-600">{hoursText(p.minutes)} · {hoursText(p.billable_minutes)} billable · <Money value={p.billable_value} /></p>
                  <p className="text-[12.5px] text-slate-600">Last note: <LastNote p={p} />{p.plan_end ? ` · Plan ends ${fmtDate(p.plan_end)}` : ''}</p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
const Fact = ({ k, v, warn }) => (
  <div className="min-w-0">
    <dt className="text-[12px] text-slate-500">{k}</dt>
    <dd className={clsx('font-semibold tabular-nums', warn ? 'text-amber-800' : 'text-slate-900')}>{v}</dd>
  </div>
);

function TeamSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading">
      <div className="flex gap-3"><Skeleton className="h-16 w-64" /><Skeleton className="h-10 w-36 ml-auto self-end" /></div>
      <Skeleton className="h-16 rounded-2xl" />
      <div className="bg-white rounded-2xl border border-slate-200/80 p-4 space-y-2.5">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}</div>
    </div>
  );
}
